// FUNCTIONALITY-101/102 — Store V2 local cart (WebView storage). No server dependency to browse.
// 102: generic promo-code registry (not a hardcoded TG50 button). TG50 stays as one rule.
// All money is whole UAH (rounded up) — see money-v2.ts.

import { getCustomerSalePrice, isFractionalUah, roundUpUah } from "./money-v2.js";

export interface CartV2Item {
  id: string;
  title: string;
  priceUah: number;
  qty: number;
  imageUrl: string | null;
  sku?: string | null;
  brand?: string | null;
  weight?: string | null;
  availabilityLabel?: string | null;
  /** CUSTOMER-R4.15-K18 — catalog stock token, not UI copy. */
  stockStatus?: string | null;
  /** COMMERCE-CANARY-163A-R1A — optional per-line cap (e.g. 2 for canary products). */
  maxQty?: number | null;
}

export interface PromoRule {
  code: string;
  valueUah: number;
  minOrderUah: number;
  label: string;
  hint: string;
}

export type PromoStatus = "none" | "applied" | "invalid" | "under_min";

export interface CartV2Totals {
  itemCount: number;
  subtotalUah: number;
  promoCode: string | null;
  promoStatus: PromoStatus;
  promoLabel: string | null;
  discountUah: number;
  minOrderUah: number | null;
  totalUah: number;
}

/** Promo registry. Add future rules here; validation stays client-side preview + server truth. */
export const PROMOS: Record<string, PromoRule> = {
  TG50: {
    code: "TG50",
    valueUah: 50,
    minOrderUah: 500,
    label: "Знижка TG50",
    hint: "−50 ₴ від 500 ₴",
  },
};

/** Kept for legacy references (cart view copy). */
export const TG50 = PROMOS.TG50 as PromoRule;

export function normalizePromoCode(raw: string | null | undefined): string {
  return String(raw ?? "").trim().toUpperCase();
}

export function findPromo(code: string | null | undefined): PromoRule | null {
  const norm = normalizePromoCode(code);
  return norm ? (PROMOS[norm] ?? null) : null;
}

const CART_KEY = "asiastyle.store-v2.cart.v1";
const PROMO_KEY = "asiastyle.store-v2.promo.v1";

function readStore<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStore(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — cart stays in memory only */
  }
}

export function getCart(): CartV2Item[] {
  const items = readStore<CartV2Item[]>(CART_KEY, []);
  if (!Array.isArray(items)) return [];
  // R7.2.2D — heal stale fractional unit prices left in WebView storage.
  return items
    .filter((i) => i && i.id && i.qty > 0 && i.priceUah > 0)
    .map((i) => ({
      ...i,
      priceUah: getCustomerSalePrice(i.priceUah),
    }))
    .filter((i) => i.priceUah > 0);
}

function setCart(items: CartV2Item[]): CartV2Item[] {
  const clean = items.filter((i) => i.qty > 0);
  writeStore(CART_KEY, clean);
  return clean;
}

/** CUSTOMER-R4.7 — write a fully merged cart (reorder merge). Does not copy payment fields. */
export function writeCart(items: CartV2Item[]): CartV2Item[] {
  return setCart(items);
}

function clampQty(item: CartV2Item, qty: number): number {
  const max = item.maxQty != null && item.maxQty > 0 ? Math.trunc(item.maxQty) : null;
  const next = Math.max(0, Math.trunc(qty));
  return max != null ? Math.min(next, max) : next;
}

export function addToCart(item: Omit<CartV2Item, "qty">, qty = 1): CartV2Item[] {
  if (!(item.priceUah > 0)) return getCart();
  // R7.2.2D — cart unit price is always Customer payable (ceil_whole_uah for >= 1).
  const priceUah = getCustomerSalePrice(item.priceUah);
  if (!(priceUah > 0)) return getCart();
  const items = getCart();
  const existing = items.find((i) => i.id === item.id);
  if (existing) {
    existing.qty = clampQty(existing, existing.qty + qty);
    // Reprice existing line to current payable unit (no stale fractional).
    if (!(existing.priceUah < 1 && priceUah >= 1)) {
      existing.priceUah = priceUah;
    }
  } else {
    items.push({ ...item, priceUah, qty: clampQty({ ...item, priceUah, qty: 0 }, qty) });
  }
  return setCart(items);
}

export function incItem(id: string): CartV2Item[] {
  const items = getCart();
  const it = items.find((i) => i.id === id);
  if (it) it.qty = clampQty(it, it.qty + 1);
  return setCart(items);
}

export function decItem(id: string): CartV2Item[] {
  const items = getCart();
  const it = items.find((i) => i.id === id);
  if (it) it.qty -= 1;
  return setCart(items);
}

export function removeItem(id: string): CartV2Item[] {
  return setCart(getCart().filter((i) => i.id !== id));
}

export function clearCart(): CartV2Item[] {
  writeStore(CART_KEY, []);
  return [];
}

/** Apply authoritative unit price from checkout revalidation (price_changed). */
export function updateItemPrice(id: string, unitPriceUah: number): CartV2Item[] {
  if (!(unitPriceUah > 0)) return getCart();
  const payable = getCustomerSalePrice(unitPriceUah);
  const items = getCart().map((i) => {
    if (i.id !== id) return i;
    // BANK-IBAN-R3.8 — 0.01 canary must never overwrite a canonical catalog unit price.
    if (payable < 1 && i.priceUah >= 1) return i;
    return { ...i, priceUah: payable };
  });
  return setCart(items);
}

export function getPromoCode(): string | null {
  const raw = normalizePromoCode(readStore<string>(PROMO_KEY, ""));
  return raw || null;
}

export function setPromoCode(code: string | null): void {
  writeStore(PROMO_KEY, code ? normalizePromoCode(code) : "");
}

export function subtotalOf(items: CartV2Item[]): number {
  // Normalize line units first so stale fractional cart lines cannot skew checkout.
  const normalized = items.map((i) => ({
    ...i,
    priceUah: getCustomerSalePrice(i.priceUah),
  }));
  const sum = normalized.reduce((s, i) => s + i.priceUah * i.qty, 0);
  // COMMERCE-176B-R1 / R7.2.2D — preserve canary lines (<1); ordinary retail is whole UAH.
  if (normalized.some((i) => isFractionalUah(i.priceUah) || i.priceUah < 1)) {
    return Math.round(sum * 100) / 100;
  }
  return roundUpUah(sum);
}

/** Evaluate a promo code against a subtotal — pure preview (server is truth at checkout). */
export function evaluatePromo(
  code: string | null | undefined,
  subtotalUah: number,
): { status: PromoStatus; promo: PromoRule | null; discountUah: number; minOrderUah: number | null } {
  const norm = normalizePromoCode(code);
  if (!norm) return { status: "none", promo: null, discountUah: 0, minOrderUah: null };
  const promo = findPromo(norm);
  if (!promo) return { status: "invalid", promo: null, discountUah: 0, minOrderUah: null };
  if (subtotalUah < promo.minOrderUah) {
    return { status: "under_min", promo, discountUah: 0, minOrderUah: promo.minOrderUah };
  }
  return { status: "applied", promo, discountUah: promo.valueUah, minOrderUah: promo.minOrderUah };
}

export function computeTotals(items: CartV2Item[], promoCode: string | null): CartV2Totals {
  const subtotalUah = subtotalOf(items);
  const itemCount = items.reduce((s, i) => s + i.qty, 0);
  const evalResult = evaluatePromo(promoCode, subtotalUah);
  const totalUah = Math.max(0, subtotalUah - evalResult.discountUah);
  return {
    itemCount,
    subtotalUah,
    promoCode: promoCode ? normalizePromoCode(promoCode) : null,
    promoStatus: evalResult.status,
    promoLabel: evalResult.promo?.label ?? null,
    discountUah: evalResult.discountUah,
    minOrderUah: evalResult.minOrderUah,
    totalUah,
  };
}
